import React, { useEffect, useRef, useState } from 'react'

import * as d3 from 'd3'
import { tileToBBOX } from '@mapbox/tilebelt'
import { createRoot } from 'react-dom/client'
import { LngLat, Point as MapboxPoint, Popup } from 'mapbox-gl'
import { EPropertyType } from '@monopolio-ui/types-library'
import {
  useFilterStore,
  useMapStore,
  usePointStore,
  usePropertiesStore
} from '@monopolio-ui/state-library'
import {
  abbreviateNumber,
  checkBoundsIntersects,
  getDesiredZoomLevel
} from '../../utils'
import PropertyPopup from './popup'
import { IPointPropertiesV2, IPoint } from '@wiggot/interfaces'

// import { useBoundStore } from '@/src/store'
// import {
//   ListingPoint,
//   ListingPointProperties
// } from '@/src/interfaces/listingInsterface'
// import {
//   checkBoundsIntersects,
//   getDesiredZoomLevel
// } from '@/src/utils/listingsTiles'
// import { abbreviateNumber } from '@mo/utils'
// import ListingPopup from './popup'
// import { useWindowSize } from '@mo/hooks'
// import { EPropertyType } from '@/src/interfaces'
// import { useAmplitude } from '@mo/web-components'
// import * as d3 from 'd3'

// const tilebelt = require('@mapbox/tilebelt')

const localStorageKey = 'listings_viewed'

const themeColors = {
  [EPropertyType.SALE]: {
    enabled: '#222',
    hover: '#97CC04',
    visited: '#ffffff'
  },
  [EPropertyType.RENT]: {
    enabled: '#222',
    hover: '#00C0C4',
    visited: '#ffffff'
  }
}

/**
 * This component is used to manage the points in the map
 * @returns {JSX.Element} - The JSX.Element
 */
const ListingsPointsManager = ({
  goToDetail
}: {
  goToDetail: (id: string) => void
}): JSX.Element => {
  const requiredTiles: string[] = []
  const {
    map,
    drawingTool,
    hoveredLayer,
    clickedLayer,
    setClickedLayer,
    setHoveredLayer
  } = useMapStore()

  const {
    pointsSvgContainer,
    isEnablePropertiesPoints,
    tileQueryParams,
    loadedPoints
  } = usePointStore()
  const { propertyType: propertyTypeStore } = useFilterStore()
  const propertyType: EPropertyType = propertyTypeStore ?? EPropertyType.SALE
  const { hoveredPropertyCard, setHoveredPropertyCard } = usePropertiesStore()

  // const windowSize = useWindowSize()
  // const windowSizeRef = useRef<typeof windowSize>(windowSize)
  // windowSizeRef.current = windowSize

  const isEnablePropertiesPointsRef = useRef<typeof isEnablePropertiesPoints>(
    isEnablePropertiesPoints
  )
  isEnablePropertiesPointsRef.current = isEnablePropertiesPoints

  const tileQueryParamsRef = useRef<typeof tileQueryParams>(tileQueryParams)
  tileQueryParamsRef.current = tileQueryParams

  const [activePoint, setActivePoint] = useState<{
    listingId: string
    instance: any
  }>()

  const activePointRef = useRef<typeof activePoint>(activePoint)
  activePointRef.current = activePoint

  const [popup, setPopup] = useState<any>(null)
  const popupRef = useRef<typeof popup>(popup)
  popupRef.current = popup

  const hoveredPropertyCardRef =
    useRef<typeof hoveredPropertyCard>(hoveredPropertyCard)
  hoveredPropertyCardRef.current = hoveredPropertyCard

  const drawingToolRef = useRef<typeof drawingTool>(drawingTool)
  drawingToolRef.current = drawingTool

  const propertyTypeRef = useRef<EPropertyType>(
    propertyType ?? EPropertyType.SALE
  )
  propertyTypeRef.current = propertyType ?? EPropertyType.SALE

  const pointsSvgContainerRef =
    useRef<typeof pointsSvgContainer>(pointsSvgContainer)
  pointsSvgContainerRef.current = pointsSvgContainer

  const hoveredLayerRef = useRef<typeof hoveredLayer>(hoveredLayer)
  hoveredLayerRef.current = hoveredLayer

  const [listingHovered, setListingHovered] = useState<{
    listingId: string | undefined
    instance: any | undefined
  }>({
    listingId: undefined,
    instance: undefined
  })

  /**
   * This function is used to remove the popup
   * @param instance The instance of the popup
   * @returns {void}
   */
  const removePopup = (): void => {
    if (!popupRef?.current) {
      return
    }
    popupRef?.current?.remove()
    setPopup(null)
  }

  /**
   * This function is used to create the HTML of the popup
   * @param id The id of the listing
   * @returns {HTMLDivElement}
   */
  const createPopupHTML = (params: {
    property: IPointPropertiesV2
    coordinates: [number, number]
  }): HTMLDivElement => {
    const { property } = params

    const divContainer = document.createElement('div')

    const root = createRoot(divContainer)
    root.render(
      <PropertyPopup
        getRef={setPopup}
        property={property}
        goToDetail={goToDetail}
        propertyType={propertyType}
        // trackEvent={trackEvent}
      />
    )

    return divContainer
  }

  /**
   * This function is used to create the listing popup
   * @param listing The listing data
   * @returns {void}
   */
  const createListingPopup = (params: {
    property: IPointPropertiesV2
    coordinates: [number, number]
  }): void => {
    if (!map) {
      return
    }

    const { property, coordinates } = params

    const popupHTML = createPopupHTML({ property, coordinates })

    const latitude = coordinates[1]
    const longitude = coordinates[0]

    new Popup({
      offset: [10, -30],
      closeButton: false,
      closeOnClick: false
    })
      ?.setLngLat({
        lat: latitude ?? 0,
        lng: longitude ?? 0
      })
      ?.setDOMContent(popupHTML)
      ?.addTo(map)
  }

  /**
   * This function is used to center the map to the listing
   * @returns {void}
   */
  const toCenter = (params: {
    lat: number
    lng: number
    withOffset: boolean
  }): void => {
    const { lat, lng, withOffset } = params
    map?.flyTo({
      center: [lng ?? 0, lat ?? 0],
      offset: [0, withOffset ? 80 : 0],
      speed: 0.7,
      duration: 300
    })
  }

  /**
   * @name registerListingInLocalStorage
   * @description This function is used to register the listing in the local storage
   * @returns {void}
   */
  const registerListingInLocalStorage = (params: {
    listingId: string
  }): void => {
    const { listingId } = params

    const isReviewedListing = verifyIfListingIsReviewedListing({
      listingId
    })

    if (isReviewedListing) {
      return
    }

    const listings = JSON.parse(
      localStorage.getItem(localStorageKey) || '[]'
    ) as string[]

    listings.push(listingId)

    localStorage.setItem(localStorageKey, JSON.stringify(listings))
  }

  /**
   * @name verifyIfListingIsReviewedListing
   * @description This function is used to verify if the listing is reviewed
   * @returns {void}
   */
  const verifyIfListingIsReviewedListing = (params: {
    listingId: string
  }): boolean => {
    const { listingId } = params
    const listings = JSON.parse(
      localStorage.getItem(localStorageKey) || '[]'
    ) as string[]

    return listings.includes(listingId)
  }

  /**
   * This function is used to project the coordinates
   * @param dValue The coordinates
   * @returns {void}
   */
  function project(dValue: number[]): MapboxPoint {
    return map?.project(new LngLat(dValue[0], dValue[1])) as MapboxPoint
  }

  /**
   * This function is used to create the listings points
   * @param spatialIndices The spatial indices of the tile (x, y, z)
   * @param features The features of the tile
   * @returns {void}
   */
  const createListingsPoints = (params: {
    key: string
    spatialIndices: {
      xIndex: number
      yIndex: number
      zIndex: number
    }
    features: IPoint[]
  }): void => {
    const {
      spatialIndices: { xIndex, yIndex, zIndex },
      features
    } = params

    const { minZoom, maxZoom } = getDesiredZoomLevel({
      zoom: zIndex
    })

    const spatialIndex = `${xIndex}-${yIndex}-${zIndex}`

    if (requiredTiles?.includes(spatialIndex) || !pointsSvgContainer) {
      return
    }

    requiredTiles?.push(spatialIndex)

    let group: any

    const createGroup = (): void => {
      group = pointsSvgContainer
        ?.append('g')
        ?.attr('id', `group-${spatialIndex}`)

      const points = group
        ?.selectAll('g.listingPoint')
        ?.data(features)
        ?.enter()
        ?.append('g')
        ?.attr('class', 'listingPoint')
        ?.attr('id', (dValue: IPoint) => {
          const { properties } = dValue
          return properties?.id
        })
        ?.on(
          'click',
          (
            dEvent: {
              currentTarget: any
              stopPropagation: () => void
            },
            dData: IPoint
          ) => {
            dEvent.stopPropagation()

            if (
              drawingToolRef?.current?.isActive &&
              drawingToolRef?.current?.status !== 'processed'
            ) {
              return
            }

            setClickedLayer({
              layerId: 'listingPoint',
              properties: undefined
            })

            removePopup()
            resetAllActivePoints()

            const point = d3?.select(dEvent?.currentTarget)

            illuminatePoint({
              pointInstance: point
            })

            const rect = point.select('rect')

            const { properties, geometry } = dData

            setActivePoint({
              listingId: properties?.id,
              instance: rect
            })

            // const phoneScreen = windowSizeRef?.current?.width < 768

            // if (phoneScreen) {
            //   setListingId(
            //     properties?.itemId ?? properties?.id,
            //     'map',
            //     properties?.scraper,
            //     properties?.providerId.toString()
            //   )
            // } else {
            //   createListingPopup({
            //     listing: properties
            //   })
            // }
            createListingPopup({
              property: properties,
              coordinates: geometry.coordinates as any as [number, number]
            })
            const dCoordinates = geometry.coordinates as any as [number, number]
            toCenter({
              lat: dCoordinates[1],
              lng: dCoordinates[0],
              // withOffset: !phoneScreen
              withOffset: true
            })

            registerListingInLocalStorage({
              listingId: properties?.id
            })
          }
        )
        ?.on('mouseover', (dEvent: any, dValue: IPoint) => {
          if (
            drawingToolRef?.current?.isActive &&
            drawingToolRef?.current?.status !== 'processed'
          ) {
            return
          }

          pointsSvgContainer?.style('cursor', 'pointer')

          setHoveredPropertyCard(undefined)
          setHoveredLayer({
            layerId: 'listingPoint',
            properties: undefined
          })

          const point = d3.select(dEvent?.currentTarget)

          setListingHovered({
            listingId: dValue?.properties?.id,
            instance: point
          })

          // setOffMarketHoveredFeature(undefined)
        })
        ?.on('mouseout', () => {
          setListingHovered({
            listingId: undefined,
            instance: undefined
          })
          setHoveredLayer(undefined)

          if (
            drawingToolRef?.current?.isActive &&
            drawingToolRef?.current?.status !== 'processed'
          ) {
            return
          }

          pointsSvgContainer?.style('cursor', 'grab')
        })

      points
        ?.append('rect')
        ?.attr('width', 58)
        ?.attr('height', 27)
        ?.style('fill', (dValue: IPoint) => {
          const { properties } = dValue
          if (
            (activePointRef &&
              activePointRef?.current?.listingId === properties?.id) ||
            hoveredPropertyCardRef?.current?.id === properties?.id
          ) {
            return themeColors[propertyTypeRef?.current]?.hover
          }

          const isReviewedListing = verifyIfListingIsReviewedListing({
            listingId: properties?.id
          })

          if (isReviewedListing) {
            return themeColors[propertyTypeRef?.current]?.visited
          }

          return themeColors[propertyTypeRef?.current]?.enabled
        })
        ?.style('stroke', '#D1D1D1')
        ?.style('stroke-width', 1)
        ?.attr('rx', 14)
        ?.attr('ry', 14)
        ?.attr('class', 'listing-point')

      points
        ?.append('text')
        ?.attr('x', 29)
        ?.attr('y', 5.5)
        ?.attr('text-anchor', 'middle')
        ?.attr('dy', '12px')
        ?.style('fill', (dValue: IPoint) => {
          const { properties } = dValue

          if (
            (activePointRef &&
              activePointRef?.current?.listingId === properties?.id) ||
            hoveredPropertyCardRef?.current?.id === properties?.id
          ) {
            return '#ffffff'
          }

          const isReviewedListing = verifyIfListingIsReviewedListing({
            listingId: properties?.id
          })

          if (isReviewedListing) {
            return '#898CA8'
          }

          return '#ffffff'
        })
        ?.attr('font-family', 'Ret Hat Display')
        ?.attr('font-weight', '700')
        ?.attr('class', 'listing-point')
        ?.text((dValue: { properties: { priceAmount: any } }) => {
          const formattedPrice = abbreviateNumber({
            value: dValue?.properties?.priceAmount
          })
          return `$${formattedPrice}`
        })
      render()
    }

    createGroup()

    function render() {
      if (
        params.key !== tileQueryParamsRef?.current ||
        !isEnablePropertiesPointsRef?.current
      ) {
        group?.remove()
        group = null

        if (hoveredLayerRef?.current?.layerId === 'listingPoint') {
          setHoveredLayer(undefined)
          pointsSvgContainerRef?.current?.style('cursor', 'grab')
        }

        map?.off('move', render)
        map?.off('moveend', render)
        return
      }

      const mapBounds: any = map?.getBounds()
      const tileBounds: any = tileToBBOX([xIndex, yIndex, zIndex])

      const intersects = checkBoundsIntersects({
        mapBounds,
        tileBounds
      })

      const currentZoom = map?.getZoom() || 0
      const isZoomed = currentZoom >= minZoom && currentZoom <= maxZoom

      if (intersects && isZoomed) {
        if (!group) {
          createGroup()
        }
        group
          ?.selectAll('g.listingPoint')
          ?.attr('transform', (dValue: { geometry: { coordinates: any } }) => {
            const coordinates = project(dValue?.geometry?.coordinates)
            return `translate(${coordinates?.x},${coordinates?.y})`
          })
      } else if (group) {
        group?.remove()
        group = null

        if (hoveredLayerRef?.current?.layerId === 'listingPoint') {
          setHoveredLayer(undefined)
          pointsSvgContainerRef?.current?.style('cursor', 'grab')
        }
      }
    }

    map?.on('move', render)
    map?.on('moveend', render)
  }

  const illuminatePoint = (params: { pointInstance: any }): void => {
    const { pointInstance } = params

    if (!pointInstance?.size()) {
      return
    }

    pointInstance?.raise()

    const rect = pointInstance?.select('rect')
    rect?.style('fill', themeColors[propertyType]?.hover)

    const text = pointInstance?.select('text')
    text?.style('fill', '#ffffff')
  }

  const turnOffAllPoints = (): void => {
    const allPoints = pointsSvgContainer?.selectAll('g.listingPoint')

    const rect = allPoints?.select('rect')
    rect?.style('fill', (dValue: IPoint) => {
      const { properties } = dValue

      if (
        activePointRef &&
        activePointRef?.current?.listingId === properties.id
      ) {
        return themeColors[propertyType]?.hover
      }

      const isReviewedListing = verifyIfListingIsReviewedListing({
        listingId: properties?.id
      })

      if (isReviewedListing) {
        return themeColors[propertyType]?.visited
      }

      return themeColors[propertyType]?.enabled
    })

    const text = allPoints?.select('text')
    text?.style('fill', (dValue: IPoint) => {
      const { properties } = dValue

      if (
        activePointRef &&
        activePointRef?.current?.listingId === properties?.id
      ) {
        return '#ffffff'
      }

      const isReviewedListing = verifyIfListingIsReviewedListing({
        listingId: properties?.id
      })

      if (isReviewedListing) {
        return '#898CA8'
      }

      return '#ffffff'
    })

    if (listingHovered?.listingId) {
      listingHovered?.instance?.raise()
    }
  }

  const changePointStyles = (): void => {
    if (!pointsSvgContainer) {
      return
    }
    const allPoints = pointsSvgContainer?.selectAll('g.listingPoint')

    const rect = allPoints?.select('rect')
    rect?.style('fill', (dValue: IPoint) => {
      const { properties } = dValue

      if (
        (activePointRef &&
          activePointRef?.current?.listingId === properties?.id) ||
        listingHovered?.listingId === properties?.id
      ) {
        return themeColors[propertyType]?.hover
      }

      const isReviewedListing = verifyIfListingIsReviewedListing({
        listingId: properties?.id
      })

      if (isReviewedListing) {
        return themeColors[propertyType]?.visited
      }

      return themeColors[propertyType]?.enabled
    })

    const text = allPoints?.select('text')
    text?.style('fill', (dValue: IPoint) => {
      const { properties } = dValue

      if (
        (activePointRef &&
          activePointRef?.current?.listingId === properties?.id) ||
        listingHovered?.listingId === properties?.id
      ) {
        return '#ffffff'
      }

      const isReviewedListing = verifyIfListingIsReviewedListing({
        listingId: properties?.id
      })

      if (isReviewedListing) {
        return '#898CA8'
      }

      return '#ffffff'
    })

    if (listingHovered?.listingId) {
      listingHovered?.instance?.raise()
    }
  }

  /**
   * This function is used to reset all the active points
   * @returns {void}
   */
  /* dValue: any */
  const resetAllActivePoints = (): void => {
    const points = d3?.selectAll('g.listingPoint')
    points?.select('rect')?.style('fill', (dValue: any) => {
      const isReviewedListing = verifyIfListingIsReviewedListing({
        listingId: dValue?.properties?.id
      })

      return isReviewedListing
        ? themeColors[propertyType]?.visited
        : themeColors[propertyType]?.enabled
    })

    points?.select('text')?.style('fill', (dValue: any) => {
      const isReviewedListing = verifyIfListingIsReviewedListing({
        listingId: dValue?.properties?.id
      })

      return isReviewedListing ? '#898CA8' : '#ffffff'
    })
  }

  /**
   * This effect is used to toggle the active point
   */
  useEffect(() => {
    const toggleActivePoint = (dEvent: any) => {
      const modal = document.getElementById('property-sheet-modal')
      const isInsideModal = modal && modal?.contains(dEvent?.target)
      if (isInsideModal) {
        return
      }

      const isInsidePopup =
        popupRef?.current && popupRef?.current?.contains(dEvent?.target)
      if (isInsidePopup) {
        return
      }

      if (activePoint) {
        resetAllActivePoints()
        setActivePoint(undefined)
      }

      if (popupRef && !popupRef?.current?.contains(dEvent?.target)) {
        removePopup()
      }
    }
    document.addEventListener('click', toggleActivePoint)

    return () => {
      document.removeEventListener('click', toggleActivePoint)
    }
    // }, [activePoint, listingId, popupRef, popup])
  }, [activePoint, popupRef, popup])

  useEffect(() => {
    if (loadedPoints?.length && pointsSvgContainer) {
      const lastGroup = loadedPoints[loadedPoints?.length - 1]
      if (!lastGroup?.features?.length) {
        return
      }
      createListingsPoints(lastGroup)
    }
  }, [loadedPoints, pointsSvgContainer])

  useEffect(() => {
    if (clickedLayer?.layerId === 'listingStacked') {
      setActivePoint(undefined)
      removePopup()
      resetAllActivePoints()
    }
  }, [clickedLayer])

  useEffect(() => {
    if (!hoveredPropertyCard?.id) {
      return
    }

    const findPoint = (id: string) => {
      const allPoints = pointsSvgContainer?.selectAll('g.listingPoint')

      const find = allPoints?.filter((dValue: any) => {
        const { properties } = dValue
        return properties?.id === id
      })

      if (!find?.size()) {
        return undefined
      }

      return find
    }

    const find = findPoint(hoveredPropertyCard?.id)

    if (!find) {
      turnOffAllPoints()
      return
    }

    setListingHovered({
      listingId: hoveredPropertyCard?.id,
      instance: find
    })
  }, [hoveredPropertyCard])

  useEffect(() => {
    changePointStyles()
  }, [listingHovered])

  useEffect(() => {
    if (hoveredLayer?.layerId !== 'listingPoint') {
      setListingHovered({
        listingId: undefined,
        instance: undefined
      })
    }
  }, [hoveredLayer])

  return <></>
}

export default ListingsPointsManager
